import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, Trash, Shield, User, Key } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
    { value: 'broker', label: 'Makler' },
    { value: 'admin', label: 'Admin' },
];

export default function UsersPage() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const [pwUser, setPwUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [savingPw, setSavingPw] = useState(false);

    const fetchUsers = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/users`, { withCredentials: true });
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Fehler beim Laden der Benutzer');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleRoleChange = async (userId, newRole) => {
        try {
            await axios.put(`${API}/api/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
            toast.success('Rolle aktualisiert');
            fetchUsers();
        } catch (error) {
            toast.error('Fehler beim Aktualisieren der Rolle');
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await axios.delete(`${API}/api/users/${deleteId}`, { withCredentials: true });
            toast.success('Benutzer gelöscht');
            fetchUsers();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
        setDeleteId(null);
    };

    const handlePasswordSave = async () => {
        if (newPassword.length < 6) {
            toast.error('Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        setSavingPw(true);
        try {
            await axios.put(`${API}/api/users/${pwUser.id}/password`, { new_password: newPassword }, { withCredentials: true });
            toast.success(`Passwort für ${pwUser.name || pwUser.email} geändert`);
            setPwUser(null);
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
        }
        setSavingPw(false);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <div className="space-y-6" data-testid="users-page">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    Benutzer
                </h1>
            </div>

            {/* Users Table */}
            <Card className="border" data-testid="users-table">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#F8F9FA]">
                            <TableHead className="font-semibold">Name</TableHead>
                            <TableHead className="font-semibold">E-Mail</TableHead>
                            <TableHead className="font-semibold">Rolle</TableHead>
                            <TableHead className="font-semibold">Erstellt am</TableHead>
                            <TableHead className="w-10"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <TableRow key={i}>
                                    {[...Array(5)].map((_, j) => (
                                        <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                                    <Users size={48} className="mx-auto mb-4 opacity-50" />
                                    <p>Keine Benutzer vorhanden</p>
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => (
                                <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                                                {user.name?.charAt(0).toUpperCase() || 'U'}
                                            </div>
                                            <span className="font-medium">{user.name}</span>
                                            {user.id === currentUser?.id && (
                                                <Badge variant="outline" className="rounded-sm text-xs">Sie</Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                                    <TableCell>
                                        <Select
                                            value={user.role}
                                            onValueChange={(value) => handleRoleChange(user.id, value)}
                                            disabled={user.id === currentUser?.id}
                                        >
                                            <SelectTrigger className="w-32 rounded-sm" data-testid={`role-select-${user.id}`}>
                                                <div className="flex items-center gap-2">
                                                    {user.role === 'admin' ? (
                                                        <Shield size={14} />
                                                    ) : (
                                                        <User size={14} />
                                                    )}
                                                    <SelectValue />
                                                </div>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ROLE_OPTIONS.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatDate(user.created_at)}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                title="Passwort ändern"
                                                onClick={() => { setPwUser(user); setNewPassword(''); setConfirmPassword(''); }}
                                                data-testid={`change-password-${user.id}`}
                                            >
                                                <Key size={16} />
                                            </Button>
                                            {user.id !== currentUser?.id && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => setDeleteId(user.id)}
                                                    data-testid={`delete-user-${user.id}`}
                                                >
                                                    <Trash size={16} />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            {/* Password Change Dialog */}
            <Dialog open={!!pwUser} onOpenChange={(open) => !open && setPwUser(null)}>
                <DialogContent data-testid="password-dialog">
                    <DialogHeader>
                        <DialogTitle>Passwort ändern</DialogTitle>
                        <DialogDescription>
                            Neues Passwort für <span className="font-medium">{pwUser?.name || pwUser?.email}</span> festlegen
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Neues Passwort</Label>
                            <Input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Mindestens 6 Zeichen"
                                className="rounded-sm"
                                data-testid="new-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Passwort bestätigen</Label>
                            <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Passwort wiederholen"
                                className="rounded-sm"
                                data-testid="confirm-password-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-sm" onClick={() => setPwUser(null)} data-testid="cancel-password-btn">
                            Abbrechen
                        </Button>
                        <Button onClick={handlePasswordSave} disabled={savingPw} className="rounded-sm" data-testid="save-password-btn">
                            {savingPw ? 'Speichern...' : 'Passwort speichern'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Diese Aktion kann nicht rückgängig gemacht werden. Der Benutzer wird dauerhaft gelöscht.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
